/**
 * Candidate popup content, coordinate actions, and review controls.
 */

import { formatAreaSqM, formatDistanceMeters } from '../lib/units.js';

export function formatCoords(lat, lon) {
    return `${Number(lat).toFixed(6)}, ${Number(lon).toFixed(6)}`;
}

export function googleMapsUrl(lat, lon) {
    return `https://www.google.com/maps?q=${lat},${lon}`;
}

export async function copyCoords(lat, lon) {
    const text = formatCoords(lat, lon);
    if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        return text;
    }

    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'absolute';
    textarea.style.left = '-9999px';
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand('copy');
    document.body.removeChild(textarea);
    return text;
}

function escapeHtml(value) {
    return String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}

function confidenceBadge(confidence) {
    const cls = confidence === 'high' ? 'success' : confidence === 'medium' ? 'warning' : 'danger';
    return `<span class="badge bg-${cls}">${escapeHtml(confidence)}</span>`;
}

function reviewStatusBadge(status) {
    const cls = status === 'confirmed'
        ? 'success'
        : status === 'rejected'
            ? 'danger'
            : status === 'needs_more_research'
                ? 'warning'
                : 'secondary';
    return `<span class="badge bg-${cls}">${escapeHtml(status || 'unreviewed')}</span>`;
}

/**
 * @param {object} candidate
 */
export function buildCandidatePopupHtml(candidate) {
    const setup = candidate.setupPoint;
    const refs = candidate.refs || [];
    const titles = candidate.refTitles || [];
    const parking = candidate.nearestParking || [];
    const warnings = candidate.warnings || [];
    const kindLabel = candidate.candidateKind === 'nfer' || candidate.n >= 3 ? '3-fer+' : '2-fer';
    const reviewStatus = candidate.reviewStatus || 'unreviewed';
    const reviewEntry = candidate.reviewEntry || {};

    const refLines = refs.map((ref, i) => {
        const title = titles[i] || '';
        return `<div><strong>${escapeHtml(ref)}</strong> — ${escapeHtml(title)}</div>`;
    }).join('');

    const parkingHtml = parking.length
        ? `<ul class="mb-2 ps-3">${parking.map((row) => `
            <li>${formatDistanceMeters(row.distanceMeters)} — ${escapeHtml(row.name || row.source)} <span class="text-muted">(${escapeHtml(row.accessStatus)})</span></li>
        `).join('')}</ul>`
        : '<p class="text-muted mb-2">No parking/trailhead within search radius.</p>';

    const warningsHtml = warnings.length
        ? `<ul class="pa-popup-warnings mb-2">${warnings.map((w) => `<li>${escapeHtml(w)}</li>`).join('')}</ul>`
        : '';

    const coords = setup ? formatCoords(setup.lat, setup.lon) : '—';
    const planning = candidate.planningScore != null
        ? `<div class="small text-muted">Planning score: ${candidate.planningScore}</div>`
        : '';
    const overrideNote = candidate.setupPointIsOverride
        ? '<div class="small text-warning">Using manual setup point override</div>'
        : '';

    const community = candidate.communityRejections || [];
    const communityHtml = community.length
        ? `<div class="pa-community-rejections mb-2">
            <div class="small fw-semibold">Community rejections (${community.length})</div>
            <ul class="small mb-0 ps-3">${community.map((row) => `
                <li><strong>${escapeHtml(row.callSign)}</strong> ${escapeHtml(row.reviewerName)}
                    ${row.notes ? `— ${escapeHtml(row.notes)}` : ''}
                    <span class="text-muted">${escapeHtml((row.submittedAt || '').slice(0, 10))}</span></li>
            `).join('')}</ul>
           </div>`
        : '';

    const defaultCallSign = candidate._defaultCallSign || '';
    const defaultName = candidate._defaultReviewerName || '';

    return `
        <div class="pa-candidate-popup">
            <div class="pa-popup-drag-handle d-flex justify-content-between align-items-start gap-2 mb-2" title="Drag to move">
                <div>
                    <div class="small text-muted">${escapeHtml(kindLabel)}</div>
                    <strong>${escapeHtml(candidate.candidateId || '')}</strong>
                </div>
                <button type="button" class="btn-close btn-close-sm" data-action="close" aria-label="Close"></button>
            </div>
            <div class="mb-2">${confidenceBadge(candidate.confidence)}
                <span class="badge bg-secondary ms-1">${escapeHtml(candidate.type || '')}</span>
                ${reviewStatusBadge(reviewStatus)}
            </div>
            <div class="pa-popup-refs mb-2">${refLines}</div>
            ${communityHtml}
            ${candidate.overlapAreaSqM ? `<div class="small mb-2">Overlap: ${formatAreaSqM(candidate.overlapAreaSqM)}</div>` : ''}
            ${planning}
            <div class="mb-2">
                <div class="small text-muted">Setup coordinate (${escapeHtml(setup?.method || '')})</div>
                <code class="pa-coord-string">${escapeHtml(coords)}</code>
                ${overrideNote}
            </div>
            <div class="d-flex flex-wrap gap-2 mb-2">
                <button type="button" class="btn btn-sm btn-outline-primary" data-action="copy" ${setup ? '' : 'disabled'}>Copy lat/lon</button>
                ${setup ? `<a class="btn btn-sm btn-outline-success" href="${googleMapsUrl(setup.lat, setup.lon)}" target="_blank" rel="noopener">Open in Google Maps</a>` : ''}
            </div>
            <div class="pa-popup-review border-top pt-2 mt-2">
                <div class="small fw-semibold mb-2">Review</div>
                <div class="d-flex flex-wrap gap-1 mb-2">
                    <button type="button" class="btn btn-sm btn-success" data-action="confirm">Confirm</button>
                    <button type="button" class="btn btn-sm btn-outline-warning" data-action="needs-research">Needs research</button>
                    <button type="button" class="btn btn-sm btn-outline-danger" data-action="reject">Submit rejection</button>
                    <button type="button" class="btn btn-sm btn-outline-secondary" data-action="reset-review">Reset</button>
                </div>
                <div class="small text-muted mb-2">Rejections are shared with everyone using this server (call sign + name required).</div>
                <div class="row g-1 mb-2">
                    <div class="col-5">
                        <label class="form-label small mb-1" for="rejectCallSign">Call sign</label>
                        <input type="text" class="form-control form-control-sm" id="rejectCallSign"
                            placeholder="e.g. W3ABC" value="${escapeHtml(defaultCallSign)}" autocomplete="off" />
                    </div>
                    <div class="col-7">
                        <label class="form-label small mb-1" for="rejectReviewerName">Name</label>
                        <input type="text" class="form-control form-control-sm" id="rejectReviewerName"
                            placeholder="Your name" value="${escapeHtml(defaultName)}" autocomplete="name" />
                    </div>
                </div>
                <label class="form-label small mb-1" for="reviewNotes">Rejection notes</label>
                <textarea id="reviewNotes" class="form-control form-control-sm mb-2" rows="2" placeholder="Parking, access, boundary concerns...">${escapeHtml(reviewEntry.notes || candidate.reviewNotes || '')}</textarea>
                <div class="small text-muted mb-1">Setup point override (lat, lon)</div>
                <div class="row g-1 mb-2">
                    <div class="col-6">
                        <input type="number" step="any" class="form-control form-control-sm" id="overrideLat"
                            placeholder="Latitude" value="${setup ? escapeHtml(setup.lat) : ''}">
                    </div>
                    <div class="col-6">
                        <input type="number" step="any" class="form-control form-control-sm" id="overrideLon"
                            placeholder="Longitude" value="${setup ? escapeHtml(setup.lon) : ''}">
                    </div>
                </div>
                <button type="button" class="btn btn-sm btn-outline-primary w-100" data-action="save-review">Save review</button>
            </div>
            <div class="small text-muted mb-1 mt-2">Nearest access (access score ${escapeHtml(candidate.accessScore || '—')})</div>
            ${parkingHtml}
            ${warningsHtml}
            ${candidate.sourceRefs?.length ? `<div class="small text-muted">Sources: ${candidate.sourceRefs.map(escapeHtml).join(', ')}</div>` : ''}
        </div>
    `;
}

/**
 * @param {HTMLElement} container
 * @param {object} candidate
 * @param {{
 *   onCopy?: (text: string) => void,
 *   onClose?: () => void,
 *   onReviewSave?: (patch: object) => void,
 *   defaultCallSign?: string,
 *   defaultReviewerName?: string,
 * }} handlers
 */
export function mountCandidatePopup(container, candidate, handlers = {}) {
    container.innerHTML = buildCandidatePopupHtml({
        ...candidate,
        _defaultCallSign: handlers.defaultCallSign || '',
        _defaultReviewerName: handlers.defaultReviewerName || '',
    });

    const setup = candidate.setupPoint;
    const copyBtn = container.querySelector('[data-action="copy"]');
    if (copyBtn && setup) {
        copyBtn.addEventListener('click', async () => {
            const text = await copyCoords(setup.lat, setup.lon);
            handlers.onCopy?.(text);
            copyBtn.textContent = 'Copied!';
            setTimeout(() => {
                copyBtn.textContent = 'Copy lat/lon';
            }, 1500);
        });
    }

    container.querySelector('[data-action="close"]')?.addEventListener('click', () => {
        handlers.onClose?.();
    });

    function readOverride() {
        const lat = Number(container.querySelector('#overrideLat')?.value);
        const lon = Number(container.querySelector('#overrideLon')?.value);
        if (!Number.isFinite(lat) || !Number.isFinite(lon)) {
            return null;
        }
        return { lat, lon };
    }

    function saveReview(patch) {
        const notes = container.querySelector('#reviewNotes')?.value?.trim() || '';
        const setupPointOverride = readOverride();
        const callSign = container.querySelector('#rejectCallSign')?.value?.trim() || '';
        const reviewerName = container.querySelector('#rejectReviewerName')?.value?.trim() || '';
        handlers.onReviewSave?.({
            ...patch,
            notes,
            callSign,
            reviewerName,
            setupPointOverride: setupPointOverride || undefined,
        });
    }

    container.querySelector('[data-action="confirm"]')?.addEventListener('click', () => {
        saveReview({ reviewStatus: 'confirmed' });
    });
    container.querySelector('[data-action="reject"]')?.addEventListener('click', () => {
        saveReview({ reviewStatus: 'rejected', submitCommunity: true });
    });
    container.querySelector('[data-action="needs-research"]')?.addEventListener('click', () => {
        saveReview({ reviewStatus: 'needs_more_research' });
    });
    container.querySelector('[data-action="reset-review"]')?.addEventListener('click', () => {
        handlers.onReviewSave?.({ reviewStatus: 'unreviewed', notes: '', setupPointOverride: null });
    });
    container.querySelector('[data-action="save-review"]')?.addEventListener('click', () => {
        saveReview({ reviewStatus: candidate.reviewStatus || 'unreviewed' });
    });
}
